import React, { useCallback, useEffect, useState } from "react";
import Wrapper from "../../../components/Wrapper";
import { labApi, hardwareApi } from "../../../services/labApi";

export default function TargetsPage() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [targets, setTargets] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [reagents, setReagents] = useState([]);
  const [hardwareList, setHardwareList] = useState([]);
  const [formData, setFormData] = useState({ 
    worker_id: "", 
    reagent_id: "", 
    hardware_id: "", 
    target_quantity: "", 
    target_type: "daily" 
  });

  const load = useCallback(async () => {
    const [targetRows, workerRows, reagentRows, hardwareRows] = await Promise.all([
      labApi.getTargetsByDate(selectedDate),
      labApi.getActiveCrew(),
      labApi.getReagents(),
      hardwareApi.getHardware(),
    ]);

    setTargets(targetRows);
    setWorkers(workerRows);
    setReagents(reagentRows);
    setHardwareList(hardwareRows);
  }, [selectedDate]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const createTarget = async (e) => {
    e.preventDefault();
    await labApi.createTarget({
      worker_id: formData.worker_id,
      reagent_id: formData.reagent_id || null,
      hardware_id: formData.hardware_id || null,
      target_quantity: Number(formData.target_quantity || 0),
      target_type: formData.target_type,
      date: selectedDate,
    });
    setFormData({ worker_id: "", reagent_id: "", hardware_id: "", target_quantity: "", target_type: "daily" });
    await load();
  };

  const removeTarget = async (id) => {
    await labApi.deleteTarget(id);
    await load();
  };

  const selectedItemValue = formData.reagent_id 
    ? `reagent_${formData.reagent_id}` 
    : formData.hardware_id 
      ? `hardware_${formData.hardware_id}` 
      : "";

  const handleItemChange = (e) => {
    const val = e.target.value;
    if (val.startsWith("reagent_")) {
      setFormData({ ...formData, reagent_id: val.replace("reagent_", ""), hardware_id: "" });
    } else if (val.startsWith("hardware_")) {
      setFormData({ ...formData, hardware_id: val.replace("hardware_", ""), reagent_id: "" });
    } else {
      setFormData({ ...formData, hardware_id: "", reagent_id: "" });
    }
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <h2 className="m-0 text-2xl font-bold text-slate-800">Targets</h2>
        <input 
          type="date" 
          value={selectedDate} 
          onChange={(e) => setSelectedDate(e.target.value)} 
          className="px-3 py-2 border rounded-lg" 
        />

        <form onSubmit={createTarget} className="grid md:grid-cols-5 gap-2 bg-white border rounded-xl p-4 shadow-sm">
          <select 
            value={formData.worker_id} 
            onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })} 
            className="px-3 py-2 border rounded-lg" 
            required
          >
            <option value="">Worker</option>
            {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>

          {/* Combined Dropdown for Reagents & Hardware */}
          <select 
            value={selectedItemValue} 
            onChange={handleItemChange} 
            className="px-3 py-2 border rounded-lg" 
            required
          >
            <option value="">Select Item...</option>
            <optgroup label="Reagents">
              {reagents.map((r) => <option key={`r-${r.id}`} value={`reagent_${r.id}`}>{r.name}</option>)}
            </optgroup>
            <optgroup label="Hardware">
              {hardwareList.map((h) => <option key={`h-${h.id}`} value={`hardware_${h.id}`}>{h.name}</option>)}
            </optgroup>
          </select>

          <input 
            value={formData.target_quantity} 
            onChange={(e) => setFormData({ ...formData, target_quantity: e.target.value })} 
            type="number" 
            min="1"
            placeholder="Quantity" 
            className="px-3 py-2 border rounded-lg" 
            required 
          />
          <select 
            value={formData.target_type} 
            onChange={(e) => setFormData({ ...formData, target_type: e.target.value })} 
            className="px-3 py-2 border rounded-lg"
          >
            <option value="daily">daily</option>
            <option value="weekly">weekly</option>
          </select>
          <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors">
            Add Target
          </button>
        </form>

        <div className="space-y-2">
          {targets.length === 0 ? (
            <p className="text-slate-500 italic py-4">No targets assigned for this date.</p>
          ) : null}
          
          {targets.map((t) => (
            <div key={t.id} className="bg-white border rounded-xl p-4 flex items-center justify-between shadow-sm">
              <div>
                <p className="m-0 font-semibold text-slate-800">
                  {t.worker_name} - {t.item_name || t.reagent_name || t.hardware_name}
                </p>
                <p className="m-0 text-sm text-slate-500 mt-0.5">
                  <span className="font-medium text-slate-700">{t.completed || 0}</span> / {t.target_quantity} completed ({t.target_type})
                </p>
              </div>
              <button 
                onClick={() => removeTarget(t.id)} 
                className="text-red-600 hover:bg-red-50 px-3 py-1.5 rounded text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
}